import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { BREAKPOINTS } from '../../lib/breakpoints'
import { useMediaQuery } from '../../lib/useMediaQuery'

export interface RowAction {
  label: string
  onSelect: () => void
  destructive?: boolean
  disabled?: boolean
  /** Shown when disabled, so a greyed-out item explains itself. */
  disabledReason?: string
}

/**
 * A row's actions, behind one trigger.
 *
 * Ported in shape from AIVY's `Admin.tsx` `RowActionsMenu`, including the two
 * decisions its own comments defend:
 *
 *   - **Portalled to `document.body`.** A `position: fixed` menu inside a
 *     scrolling table is clipped by any ancestor with `overflow` — which is
 *     every table wrapper — and a menu that opens invisible is indistinguishable
 *     from a dead button.
 *   - **Focus moves to the first item on open**, and Escape returns it.
 *
 * Dropped from that port: the `active` prop and its two `useOnDeactivate` calls,
 * which exist to coordinate with AIVY's swipe-stack (§5.1).
 *
 * Below 768px it becomes a bottom sheet — the same items, at thumb height,
 * where a portalled dropdown anchored to a row would be unreachable.
 *
 * ── THE SHEET IS A REAL `<dialog>`, THE DROPDOWN IS NOT ─────────────────────
 *
 * Deliberately divergent, because the two are different things. On a phone this
 * IS a modal sheet: it covers the screen behind a scrim and the table under it
 * must not be reachable. On a desktop it is a dropdown, and a modal dropdown
 * would be wrong — clicking outside should dismiss it, not be blocked.
 *
 * Until Stage 7b the sheet was a bare `<div>` behind a hand-rolled
 * `.row-menu__scrim`, which made it modal to touch and a plain menu to a screen
 * reader: the table behind the scrim was still reachable by swipe. `inert`
 * appeared nowhere in the repo. `showModal()` gives the inert background, the
 * backdrop and Escape for free, which is the same argument `AppShell` makes for
 * the navigation drawer and `ConfirmDialog` makes for itself.
 *
 * `role="menu"` stays on an inner element rather than on the `<dialog>`: an
 * explicit role would override the dialog role and take the modal semantics
 * with it, which is the whole reason for the change.
 */
export function RowActions({ label, actions }: { label: string; actions: RowAction[] }) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDialogElement>(null)
  const isWide = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`)

  /**
   * Close when the layout crosses the breakpoint, as an adjustment DURING
   * RENDER rather than in an effect — React's documented answer to "reset state
   * when an input changes", and the same shape `AppShell` uses for the drawer.
   *
   * It is not tidiness. Rotating a phone crosses 768px, and the open surface on
   * the far side of that line is a different element: an unmounted open
   * `<dialog>` fires no `close` event, so anything cleaning up on close would
   * never run, and the anchor captured for the dropdown belongs to a layout
   * that no longer exists.
   */
  const [wasWide, setWasWide] = useState(isWide)
  if (wasWide !== isWide) {
    setWasWide(isWide)
    if (open) {
      setOpen(false)
      setAnchor(null)
    }
  }

  /**
   * Closing always hands focus back; `triggerRef` outlives the portal.
   *
   * **The `close()` has to come first**, and that is not tidiness. While a
   * `<dialog>` is modal everything outside it is inert, so `.focus()` on the
   * trigger is a silent no-op — and because the sheet is rendered only while
   * open, `setOpen(false)` UNMOUNTS it rather than closing it, which fires no
   * `close` event either (trap 46). Both routes to restoring focus were shut at
   * once, and Escape left focus nowhere. Closing through the dialog leaves the
   * top layer synchronously, so the `.focus()` below lands.
   *
   * Idempotent in every direction: `close()` on a non-open or non-existent
   * dialog does nothing, and this is also what `onClose` calls, so the native
   * Escape and backdrop routes converge here rather than racing it.
   */
  const closeAndRestore = useCallback(() => {
    sheetRef.current?.close()
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  /**
   * DECLARED BEFORE the focus effect below, and the order is load-bearing.
   * `showModal()` runs the dialog focusing steps, which land on the first
   * focusable descendant -- and since the items are `aria-disabled` rather than
   * natively `disabled`, that is the first item whether or not it can be
   * chosen. Opening the dialog first and setting focus second is what keeps the
   * entry point on an item a keyboard user can actually use.
   */
  /**
   * Drive `showModal()` from state rather than calling it in the handler, so the
   * two cannot disagree — `AppShell.tsx:47` is the precedent and states the
   * reason. The dialog is rendered only while open, so this effect is what
   * opens it, one commit after it mounts.
   */
  useEffect(() => {
    const dialog = sheetRef.current
    if (dialog && !isWide && open && !dialog.open) dialog.showModal()

    /**
     * The cleanup carries the focus restore as well as the close, because
     * CROSSING THE BREAKPOINT tears this surface down without going through
     * any of the close routes.
     *
     * The render-time adjustment above sets `setOpen(false)`, which unmounts
     * whichever surface was open — and on a phone that crossing is a rotation.
     * Measured in both directions on the production bundle: focus on `<body>`
     * afterwards. It is exactly the defect `AppShell` is fixed for a few files
     * away, and the paragraph above diagnoses it in words; the focus half
     * simply was not applied here.
     *
     * Unlike `AppShell`, the trigger SURVIVES the crossing — the row is still
     * there — so there is a real target rather than a fallback. Guarded on
     * `<body>` so a normal close, which has already put focus on the trigger,
     * and a `select()` whose action has opened something else, are both left
     * alone.
     */
    return () => {
      if (dialog?.open) dialog.close()
      // Read at cleanup time deliberately, which is what the rule below warns
      // about and what this wants: on a breakpoint crossing the row survives, so
      // the ref still points at the trigger; on a real unmount React has already
      // detached it and the optional chain is the whole guard. `AppShell` makes
      // the same trade for the same reason.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (open && document.activeElement === document.body) triggerRef.current?.focus()
    }
  }, [open, isWide])

  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus()

    /**
     * The keyboard half of `role="menu"`, which this component declared and did
     * not implement until Stage 7.
     *
     * A menu promises arrow-key navigation between its items and promises that
     * Tab does not walk through them one at a time. Neither was true here, and
     * the second is worse than it sounds: the menu is portalled to
     * `document.body`, so Tab from an item did not land on the next row's
     * trigger — it landed at the END of the document, past everything. axe has
     * no rule for either, which is why §7.2 asks for keyboard navigation in
     * words rather than leaving it to the sweep.
     */
    /**
     * EVERY item, including the disabled ones — and that is the §7.2 fix.
     *
     * This used to exclude `[aria-disabled="true"]`, so a disabled item had no
     * keyboard route at all: the arrows skipped it, Tab is intercepted below,
     * and `role="menu"` puts a screen reader into application mode, which
     * removes the virtual-cursor route too. `.row-menu__why` — the sentence
     * explaining WHY an item is greyed out — was mouse-only content.
     *
     * Refuse ACTIVATION, not focus: `select()` returns early on a disabled
     * action, which is where the refusal belongs. The items are `aria-disabled`
     * rather than natively `disabled` precisely so they stay focusable.
     *
     * Initial focus still lands on the first ENABLED item (above): entering a
     * menu on something that cannot be used is worse, and the clause is about
     * reachability, not about the entry point.
     */
    const ring = () => [
      ...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
    ]

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeAndRestore()
        return
      }
      const inMenu = menuRef.current?.contains(document.activeElement)
      if (!inMenu) return

      if (e.key === 'Tab') {
        // Leave the menu rather than tabbing into whatever the portal put us
        // next to. The trigger is where the user was.
        e.preventDefault()
        closeAndRestore()
        return
      }

      const items = ring()
      if (items.length === 0) return
      const at = items.indexOf(document.activeElement as HTMLElement)

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const step = e.key === 'ArrowDown' ? 1 : -1
        // Wrapping, per the ARIA pattern: a menu is a ring, not a list with
        // ends a keyboard user has to discover.
        items[(at + step + items.length) % items.length]?.focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        items[0]?.focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        items[items.length - 1]?.focus()
      }
    }
    const onPointer = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) {
        /**
         * This was the ONE close route in the component that dropped focus,
         * and it is the commonest there is: tapping outside is *the* dismissal
         * gesture for a bottom sheet. Measured on the production bundle at
         * 390x844 in both engines — sheet gone, `document.activeElement` on
         * `<body>`. A bare `setOpen(false)` UNMOUNTS the dialog rather than
         * closing it, so no `close` event fires and `onClose` never runs (trap
         * 46) — which two comments in this file claimed covered every route
         * that skips React. They described a guarantee this path did not have.
         *
         * NOT `closeAndRestore()`, though, and the difference is the whole
         * point of doing it here. That helper restores unconditionally, which
         * is right for Escape and for Tab — the user asked to leave — and wrong
         * for a pointer: on a desktop, clicking into another control would yank
         * focus back to a trigger the user had just navigated away from.
         *
         * So: leave the top layer, then restore only if focus would otherwise
         * be nowhere. A backdrop tap lands on the dialog's own `::backdrop`,
         * which takes no focus, so the phone case always restores; a click onto
         * a real control keeps that control.
         */
        sheetRef.current?.close()
        setOpen(false)
        /**
         * DECIDED AFTER THE FRAME, and the first version of this fix was not.
         *
         * This handler runs on `pointerdown`, in the capture phase — which
         * fires BEFORE the browser moves focus, since focus moves on
         * `mousedown`'s default action. Read synchronously here,
         * `document.activeElement` is still the menu item: not `<body>`, and
         * not the control being clicked into. The two cases this guard exists
         * to separate are indistinguishable at that instant, and the guard was
         * simply false in both — so the backdrop tap it was written for went on
         * stranding focus. Measured: still `<body>` after the tap.
         *
         * One frame later the browser has settled focus, and the question is
         * answerable: `<body>` means the pointer landed on nothing focusable
         * (the sheet's `::backdrop`, or empty space on a desktop), and anything
         * else means the user has chosen where to be and should keep it.
         *
         * This is trap 54's lesson applied rather than quoted: a timing probe
         * is only as good as the last moment it looked.
         */
        requestAnimationFrame(() => {
          if (document.activeElement === document.body) triggerRef.current?.focus()
        })
      }
    }
    // Capture phase: a menu item's own click must still land, but a click
    // anywhere else should close before that element reacts.
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer, true)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer, true)
    }
  }, [open, closeAndRestore])

  const toggle = () => {
    if (!open && triggerRef.current) setAnchor(triggerRef.current.getBoundingClientRect())
    setOpen((o) => !o)
  }

  const select = (action: RowAction) => {
    if (action.disabled) return
    /**
     * FOCUS THE TRIGGER BEFORE THE ACTION RUNS, and the order is the whole
     * point.
     *
     * `setOpen(false)` and `action.onSelect()` land in one React batch, so the
     * menuitem holding focus is gone by the time anything the action opens has
     * mounted. Measured on `/admin/users`: activating Suspend from the keyboard
     * put `ConfirmDialog`'s `returnTo` — which reads `document.activeElement` in
     * a mount effect — at `<body>`, so closing that dialog returned focus
     * nowhere. The actions with no dialog at all (Reactivate, Restore) simply
     * dropped it.
     *
     * `.focus()` here is synchronous and the menu is still in the DOM, so the
     * trigger genuinely holds focus before the batch commits. The trigger
     * survives the commit; the item does not.
     *
     * `closeAndRestore()` rather than a bare `setOpen(false)`, because on a
     * phone this menu is a modal `<dialog>` and the trigger is inert until it
     * leaves the top layer. See that function.
     */
    closeAndRestore()
    action.onSelect()
  }

  const itemButtons: ReactNode = actions.map((action) => (
    <button
      key={action.label}
      type="button"
      role="menuitem"
      className={`row-menu__item${action.destructive ? ' row-menu__item--destructive' : ''}`}
      aria-disabled={action.disabled || undefined}
      title={action.disabled ? action.disabledReason : undefined}
      onClick={() => select(action)}
    >
      {action.label}
      {action.disabled && action.disabledReason && (
        <span className="row-menu__why">{action.disabledReason}</span>
      )}
    </button>
  ))

  /**
   * The dropdown: a plain portalled box, anchored under the trigger.
   *
   * CLAMPED to the viewport, which it was not. `top: anchor.bottom + 6` with no
   * bound puts a five-item menu off the bottom of a short viewport with nothing
   * to scroll — and a landscape phone is 844px wide, so it takes THIS branch
   * rather than the sheet. Rotating a phone therefore changed which surface
   * opened, to the one with no height handling at all.
   *
   * `maxHeight` rather than a flip: the box scrolls, and a scrolling menu is
   * legible where a flipped one that still does not fit is not.
   */
  const dropdown = (
    <div
      ref={menuRef}
      role="menu"
      aria-label={label}
      className="row-menu"
      style={
        anchor
          ? {
              top: anchor.bottom + 6,
              right: Math.max(8, window.innerWidth - anchor.right),
              maxHeight: Math.max(120, window.innerHeight - anchor.bottom - 16),
            }
          : undefined
      }
    >
      {itemButtons}
    </div>
  )

  /**
   * The sheet: a real modal `<dialog>`, so the inert background, the backdrop
   * and Escape are the platform's. See the component header for why this branch
   * is modal and the one above is not.
   *
   * `onClose` covers the routes that never go through React — Escape and the
   * backdrop close a `<dialog>` without telling it (trap 34) — and the keydown
   * handler covers the rest. Both call the same idempotent restore.
   */
  const sheet = (
    <dialog
      ref={sheetRef}
      className="row-menu--sheet"
      /* "Actions", not `label`. The inner `role="menu"` already carries the
         full "Actions for <email>", and naming both made a screen reader
         announce the same string twice, one gesture apart. Dropping the menu's
         name instead would leave the menu unnamed, which is worse. */
      aria-label="Actions"
      onClose={closeAndRestore}
    >
      <div ref={menuRef} role="menu" aria-label={label} className="row-menu__items">
        {itemButtons}
      </div>
    </dialog>
  )

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <DotsIcon />
        <span className="visually-hidden">{label}</span>
      </button>
      {open && createPortal(isWide ? dropdown : sheet, document.body)}
    </>
  )
}

function DotsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  )
}
